import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormDropdown,
    FormFields,
    FormMultiselectDropdown,
    FormTextField,
    FormValidationSummary,
    mapToDropdownOption,
    Stack
} from "sprint-common-web";

import { FormDistributorSearchDropdown } from "../../../../common/components/form/FormDistributorDropdown";
import { AncillaryItem } from "../AncillaryItem";
import { CreateAncillaryFormInputs } from "./CreateAncillaryFormInputs";
import { useCreateAncillaryForm } from "./useCreateAncillaryForm";

export interface CreateAncillaryFormProps {
    onCancel(): void;
    onSubmit(newAncillary: AncillaryItem): void;
}

export const CreateAncillaryForm: React.FC<CreateAncillaryFormProps> = (props: CreateAncillaryFormProps) => {
    const { errors, control, formState, handleSubmit, reset, watch } = useForm<CreateAncillaryFormInputs>({
        defaultValues: {
            title: "",
            airlineIds: [],
            distributorId: "",
            versionTypeId: ""
        },
        criteriaMode: "all"
    });

    const values = watch();

    const {
        airlines,
        versionTypes,
        createNewAncillary,
        validationResult,
        setValidationResult
    } = useCreateAncillaryForm();

    useEffect(() => {
        if (validationResult) {
            setValidationResult(undefined);
        }
    }, [values.airlineIds, values.distributorId, values.versionTypeId, values.title]);

    const createAncillary = async (inputs: CreateAncillaryFormInputs) => {
        const newItem = await createNewAncillary(inputs);

        if (!newItem) {
            return;
        }

        props.onSubmit(newItem);
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        props.onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(createAncillary)}>
            <FormFields>
                <Stack>
                    <FormTextField
                        label="Title"
                        control={control}
                        name="title"
                        errors={errors}
                        rules={{ required: "Enter correct title" }}
                    />
                    <FormMultiselectDropdown
                        options={airlines.map((item) => mapToDropdownOption(item))}
                        control={control}
                        errors={errors}
                        rules={{ required: "Search and select airlines" }}
                        name="airlineIds"
                        label="Airlines"
                    />
                    <FormDropdown
                        options={versionTypes.map((item) => mapToDropdownOption(item))}
                        control={control}
                        errors={errors}
                        rules={{ required: "Search and select version type" }}
                        name="versionTypeId"
                        label="Version Type"
                    />
                    <FormDistributorSearchDropdown
                        control={control}
                        errors={errors}
                        rules={{ required: "Search and select distributor" }}
                        name="distributorId"
                        label="Distributor"
                        placeholder={"Distributor"}
                    />

                    <FormValidationSummary
                        validationResult={validationResult}
                        styles={{ root: { padding: 0, marginTop: 10 } }}
                    />
                </Stack>
            </FormFields>
            <FormActions
                isLoading={formState.isValidating || formState.isSubmitting}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};
